import { forwardRef, useEffect, useState } from "react";

import useZodValidation from "../../../hooks/useZodValidation";
import { emailSchema } from "./email.schema";

import ResponseMessage from "@/components/Error/ResponseMessage";
import { Spacer, VStack } from "@/components/Layout";
import Icon from "../../Icon/Icon";
import Input from "../Input";

type InputEmailProps = {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  width?: number | string;
  errorMessageType?: "error" | "errorCard" | "errorBalloon";
  errorMessage?: string;
  placeholder?: string;
  size?: "xs" | "sm" | "md";
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
};

export const InputEmail = forwardRef<HTMLInputElement, InputEmailProps>(
  (
    {
      value,
      onChange,
      onBlur,
      width = 260,
      errorMessageType,
      errorMessage,
      placeholder,
      size,
      disabled,
      readOnly,
      required,
      className,
    },
    ref,
  ) => {
    useZodValidation();
    const [error, setError] = useState<string | null>(errorMessage ?? null);
    const [inputWidth, setInputWidth] = useState<number | string>(width);

    const WIDTH_PER_CHAR = 10;
    const MAX_WIDTH = 500;

    useEffect(() => {
      setError(errorMessage ?? null);
    }, [errorMessage]);

    useEffect(() => {
      const newWidth = Math.min(
        Math.max((value?.length ?? 0) * WIDTH_PER_CHAR, Number(width)),
        MAX_WIDTH,
      );
      setInputWidth(newWidth);
    }, [value, width]);

    const validate = (value: string): string | null => {
      const result = (
        required
          ? // string()では空文字が許容されるのでここで弾く
            emailSchema.refine((val) => val !== "", {
              params: {
                i18n: {
                  key: "custom.required",
                },
              },
            })
          : emailSchema.nullable().optional()
      ).safeParse(value);
      if (!result.success) {
        const issue = result.error.issues[0];
        return issue.message;
      }
      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const error = validate(newValue);
      onChange(newValue);
      setError(error);
    };

    return (
      <VStack className={className}>
        <Input
          ref={ref}
          type="email"
          prefix={<Icon iconName="Mail" />}
          value={value}
          width={inputWidth}
          onChange={handleChange}
          onBlur={onBlur}
          error={!!error}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
        />
        {error && (
          <>
            <Spacer variant="xxs" />
            <ResponseMessage type={errorMessageType} size={size}>
              {error}
            </ResponseMessage>
          </>
        )}
      </VStack>
    );
  },
);

InputEmail.displayName = "InputEmail";
